import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Lock, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useRobots } from "@/components/seo/Seo";
import adminApi, { apiError } from "@/lib/adminApi";
import Logo from "@/components/Logo";
import { T } from "@/components/admin/ui";

export default function AdminLogin() {
  useRobots("noindex, nofollow");
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    document.title = "MILES Operations — Connexion";
    let robots = document.querySelector('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement("meta");
      robots.setAttribute("name", "robots");
      document.head.appendChild(robots);
    }
    robots.setAttribute("content", "noindex, nofollow");
    adminApi.get("/auth/me").then(() => navigate("/admin/app", { replace: true })).catch(() => {});
  }, [navigate]);

  const submit = async (event) => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await adminApi.post("/auth/login", { email: email.trim(), password });
      navigate("/admin/app", { replace: true });
    } catch (error) {
      toast.error("Connexion impossible", { description: apiError(error, "Vérifiez vos identifiants.") });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-cloud px-5" data-testid="admin-login-page">
      {/* atmosphère MILES — halos + route line abstraite */}
      <div className="pointer-events-none absolute -left-40 -top-40 h-[480px] w-[480px] rounded-full bg-miles/[0.07] blur-3xl" />
      <div className="pointer-events-none absolute -bottom-52 -right-40 h-[520px] w-[520px] rounded-full bg-miles-electric/[0.08] blur-3xl" />
      <svg className="pointer-events-none absolute left-1/2 top-0 hidden h-full w-[560px] -translate-x-1/2 opacity-[0.35] lg:block" viewBox="0 0 560 900" fill="none" aria-hidden="true">
        <path d="M280 -20 C 200 220, 380 420, 280 920" stroke="#2457FF" strokeOpacity="0.14" strokeWidth="1.5" strokeDasharray="3 8" />
        <circle cx="280" cy="120" r="5" fill="#2457FF" fillOpacity="0.25" />
        <circle cx="296" cy="480" r="4" fill="#0B1F4D" fillOpacity="0.2" />
        <circle cx="272" cy="760" r="5" fill="#2457FF" fillOpacity="0.18" />
      </svg>

      <form onSubmit={submit} data-testid="admin-login-form"
        className="relative w-full max-w-[420px] rounded-3xl border border-ink/[0.07] bg-white/90 p-9 shadow-[0_24px_60px_rgba(11,31,77,0.10)] backdrop-blur-xl sm:p-10">
        <Logo variant="blue" className="h-6" testId="login-logo" />
        <h1 className="mt-8 text-[26px] font-semibold tracking-tight text-ink">Bienvenue dans MILES Operations</h1>
        <p className="mt-1.5 text-sm leading-relaxed text-slate">Pilotez réservations, chauffeurs et opérations.</p>

        <label className="mt-7 block text-[13px] font-medium text-ink" htmlFor="admin-email">E-mail</label>
        <input id="admin-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder="vous@gomiles.ch" autoComplete="username"
          data-testid="admin-email-input" className={`${T.input} mt-1.5`} />
        <label className="mt-4 block text-[13px] font-medium text-ink" htmlFor="admin-password">Mot de passe</label>
        <input id="admin-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
          placeholder="••••••••••••" autoComplete="current-password"
          data-testid="admin-password-input" className={`${T.input} mt-1.5`} />

        <button type="submit" disabled={busy} data-testid="admin-login-button"
          className="mt-6 flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-miles text-[15px] font-semibold text-white shadow-[0_6px_18px_rgba(36,87,255,0.28)] transition-all duration-150 hover:bg-miles-dark active:scale-[0.99] disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" strokeWidth={1.5} />}
          {busy ? "Connexion…" : "Se connecter"}
        </button>

        <p className="mt-6 flex items-center justify-center gap-1.5 text-xs text-slate/70">
          <ShieldCheck className="h-3.5 w-3.5 text-miles" strokeWidth={1.5} />
          Accès sécurisé — sessions chiffrées, journal d'audit
        </p>
      </form>
    </div>
  );
}
